import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import servicesStrings from 'modules/services/strings';
import { categories } from 'constants/context';

import Modal from 'modules/common/components/Modal/Modal';
import AlternativeNamesModal from '../AlternativeNamesModal';
import {
  Wrapper,
  GridWrapper,
  Header,
  Divider,
  ItemColumn,
  ItemRow,
  ItemText,
  ItemBtn,
  ItemStatusText,
  MoreButton,
  PreparationList,
  PreparationListItem,
  TruncatedTextWrapper,
  MoreTextWrapper,
  CptCodeText,
  ShowAlternativeButton,
} from './ServicesList.style';
import AddToPopularButton from './AddToPopularButton';

const TruncatedText = ({ modalTitle, children }) => {
  const containerRef = useRef(null);
  const [isShowMore, setIsShowMore] = useState(false);
  const [showMoreModal, setShowMoreModal] = useState(false);
  useEffect(() => {
    if (containerRef.current) {
      const containerHeight = containerRef.current.clientHeight;
      const containerScrollHeight = containerRef.current.scrollHeight;
      setIsShowMore(containerScrollHeight > containerHeight);
    }
  }, [children]);
  return (
    <>
      <TruncatedTextWrapper ref={containerRef}>{children}</TruncatedTextWrapper>
      {isShowMore && <MoreButton onClick={() => setShowMoreModal(true)}>... More</MoreButton>}
      {showMoreModal && (
        <Modal
          data-testid="PreparationListModal"
          title={modalTitle}
          onClose={() => setShowMoreModal(false)}
          toggle={showMoreModal}
          actionsDisabled
        >
          <MoreTextWrapper>{children}</MoreTextWrapper>
        </Modal>
      )}
    </>
  );
};

TruncatedText.propTypes = {
  modalTitle: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const ServicesList = ({
  servicesListArr,
  handleEditServiceBtnClick,
  handleDeleteServiceBtnClick,
  specialitiesList,
  subContext,
}) => {
  const [copiedText, setCopiedText] = useState('');
  const [showAlternativeModal, setShowAlternativeModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const servicesGridHeaderList = servicesStrings.servicesList.servicesGridHeader;
  const statusName = {
    active: 'Active',
    inactive: 'In-Active',
  };

  const handleShowAlternativeClick = service => {
    setSelectedService(service);
    setShowAlternativeModal(true);
  };

  return (
    <Wrapper>
      <GridWrapper>
        <Divider />
        {Object.keys(servicesGridHeaderList).map(servicesGridHeaderKey => (
          <Header key={servicesGridHeaderKey}>
            {servicesGridHeaderList[servicesGridHeaderKey]}
          </Header>
        ))}
        <Divider />
        {servicesListArr?.map(service => (
          <React.Fragment key={service.serviceKey}>
            <ItemColumn>
              <CptCodeText
                copied={copiedText === service.cpt}
                onClick={() => {
                  navigator.clipboard.writeText(service.cpt);
                  setCopiedText(service.cpt);
                }}
              >
                {service.cpt}
              </CptCodeText>
            </ItemColumn>
            <ItemColumn>
              <TruncatedText modalTitle={servicesGridHeaderList.servicesNameGridHeader}>
                <ItemText>{service.serviceNameEn}</ItemText>
                <ItemText dir="rtl">{service.serviceNameAr}</ItemText>
              </TruncatedText>
              {(subContext === categories.labs || subContext === categories.scans) && (
                <ShowAlternativeButton onClick={() => handleShowAlternativeClick(service)}>
                  + {servicesStrings.alternativeNames.showAlternativeButton}
                </ShowAlternativeButton>
              )}
            </ItemColumn>
            <ItemColumn>
              <AddToPopularButton
                value={service.isPopular}
                onClick={() =>
                  handleEditServiceBtnClick({ ...service, isPopular: !service.isPopular })
                }
              />
            </ItemColumn>
            <ItemColumn>
              <TruncatedText modalTitle={servicesGridHeaderList.servicesMasterServiceGridHeader}>
                <ItemText>{service.masterService?.masterServiceNameEn}</ItemText>
              </TruncatedText>
            </ItemColumn>
            <ItemColumn>
              <TruncatedText modalTitle={servicesGridHeaderList.servicesSpecialityGridHeader}>
                <ItemText>
                  {service.serviceSpecialities
                    .map(
                      speciality =>
                        specialitiesList?.find(
                          spec => spec.SpecialityKey === speciality.specialityKey,
                        )?.SpecialityNameEnglish,
                    )
                    .join(', ')}
                </ItemText>
              </TruncatedText>
            </ItemColumn>
            <ItemColumn>
              <TruncatedText modalTitle={servicesGridHeaderList.servicesDetailsGridHeader}>
                <ItemText>{service.details}</ItemText>
              </TruncatedText>
            </ItemColumn>
            <ItemColumn>
              <TruncatedText modalTitle={servicesGridHeaderList.servicesPreparationGridHeader}>
                <PreparationList>
                  {service.servicePreparations.length > 0 &&
                    service.servicePreparations.map(item => (
                      <PreparationListItem>{item.preparationNameEn}</PreparationListItem>
                    ))}
                  {service.servicePreparations.length > 0 &&
                    service.servicePreparations.map(item => (
                      <PreparationListItem dir="rtl">{item.preparationNameAr}</PreparationListItem>
                    ))}
                </PreparationList>
              </TruncatedText>
            </ItemColumn>
            <ItemColumn>
              <ItemStatusText isActive={service.statusKey === 'active'}>
                {statusName[service.statusKey]}
              </ItemStatusText>
            </ItemColumn>
            <ItemRow>
              <ItemBtn
                as="button"
                icon={'"\\e90a"'}
                onClick={() => handleEditServiceBtnClick(service)}
              />
              <ItemBtn
                as="button"
                icon={'"\\e91f"'}
                onClick={() => handleDeleteServiceBtnClick(service)}
              />
            </ItemRow>
            <Divider />
          </React.Fragment>
        ))}
      </GridWrapper>
      <AlternativeNamesModal
        key={selectedService?.serviceKey}
        isOpen={showAlternativeModal}
        onClose={() => setShowAlternativeModal(false)}
        service={selectedService}
      />
    </Wrapper>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

ServicesList.propTypes = {
  servicesListArr: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  handleEditServiceBtnClick: PropTypes.func.isRequired,
  handleDeleteServiceBtnClick: PropTypes.func.isRequired,
  specialitiesList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  subContext: PropTypes.string,
};

ServicesList.defaultProps = {
  subContext: null,
};

export default ServicesList;
